const STATUS_MAP = {
  pending:             { bg: '#FEF3C7', color: '#92400E', label: 'Pending' },
  confirmed:           { bg: '#DBEAFE', color: '#1E40AF', label: 'Confirmed' },
  'in-progress':       { bg: '#EDE9FE', color: '#5B21B6', label: 'In Progress' },
  inprogress:          { bg: '#EDE9FE', color: '#5B21B6', label: 'In Progress' },
  completed:           { bg: '#D1FAE5', color: '#065F46', label: 'Completed' },
  cancelled:           { bg: '#FEE2E2', color: '#991B1B', label: 'Cancelled' },
  noshow:              { bg: '#F3F4F6', color: '#374151', label: 'No Show' },
  'no-show':           { bg: '#F3F4F6', color: '#374151', label: 'No Show' },
  processing:          { bg: '#FEF3C7', color: '#92400E', label: 'Processing' },
  ready:               { bg: '#DBEAFE', color: '#1E40AF', label: 'Ready' },
  'ready-for-pickup':  { bg: '#D1FAE5', color: '#065F46', label: 'Ready for Pickup' },
};

const RANK_COLORS = ['#F59E0B', '#9CA3AF', '#B08D57', '#6B7280', '#6B7280'];

const EMPTY_VALUE = '—';

// Report data includes values submitted through public booking and order forms.
// Treat every dynamic value as untrusted before inserting it into generated HTML.
export const escapeHtml = (value) => String(value ?? '')
  .replace(/&/g, '&amp;')
  .replace(/</g, '&lt;')
  .replace(/>/g, '&gt;')
  .replace(/"/g, '&quot;')
  .replace(/'/g, '&#39;');

const toFiniteNumber = (value, fallback = 0) => {
  const number = Number(value);
  return Number.isFinite(number) ? number : fallback;
};

const badge = (status) => {
  const normalizedStatus = String(status ?? '').toLowerCase();
  const s = STATUS_MAP[normalizedStatus] ?? { bg: '#F3F4F6', color: '#374151', label: status ?? EMPTY_VALUE };
  return `<span style="display:inline-block;padding:3px 10px;border-radius:999px;background:${s.bg};color:${s.color};font-size:11px;font-weight:700;letter-spacing:0.02em;">${escapeHtml(s.label)}</span>`;
};

const fmtDate = (d) => {
  if (!d) return EMPTY_VALUE;

  const date = new Date(d);
  if (Number.isNaN(date.getTime())) return EMPTY_VALUE;

  return date.toLocaleDateString('en-GB', { day: 'numeric', month: 'short', year: 'numeric' });
};

const fmtCurrency = (n) =>
  `GH¢${toFiniteNumber(n).toLocaleString('en-GH', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;

const filterLabel = (filters = {}) => {
  const parts = [];
  const rangeMap = { upcoming: 'Upcoming', today: 'Today', past: 'Past', this_week: 'This week', this_month: 'This month' };
  const dateRange = typeof filters.dateRange === 'string' ? filters.dateRange : '';
  const status = typeof filters.status === 'string' ? filters.status : '';

  if (dateRange) {
    parts.push(dateRange === 'custom'
      ? `${filters.startDate || '…'} – ${filters.endDate || '…'}`
      : (rangeMap[dateRange] ?? dateRange));
  }
  parts.push(status && status !== 'all'
    ? status.charAt(0).toUpperCase() + status.slice(1)
    : 'All statuses');
  return parts.join(' · ');
};

// ── Top performers helpers ──────────────────────────────────────────────────

const topOrderedItems = (activeOrders, topN = 5) => {
  const counts = new Map();
  for (const order of activeOrders) {
    for (const item of (Array.isArray(order?.items) ? order.items : [])) {
      const name = String(item?.itemName ?? item?.name ?? 'Unknown');
      counts.set(name, (counts.get(name) ?? 0) + toFiniteNumber(item?.quantity, 1));
    }
  }
  return [...counts.entries()]
    .sort(([, a], [, b]) => b - a)
    .slice(0, topN)
    .map(([name, qty]) => ({ name, count: qty, unit: 'units ordered' }));
};

const topBookedServices = (activeBookings, topN = 5) => {
  const counts = new Map();
  for (const b of activeBookings) {
    const name = String(b?.serviceName ?? 'Unknown');
    counts.set(name, (counts.get(name) ?? 0) + 1);
  }
  return [...counts.entries()]
    .sort(([, a], [, b]) => b - a)
    .slice(0, topN)
    .map(([name, count]) => ({ name, count, unit: count === 1 ? 'booking' : 'bookings' }));
};

const renderTopSection = (items, sectionTitle) => {
  if (!items.length) return '';
  const max = items[0].count;

  const rows = items.map(({ name, count, unit }, i) => {
    const pct   = Math.round((count / max) * 100);
    const color = RANK_COLORS[i] ?? '#6B7280';
    const isTop = i === 0;
    return `
      <div style="display:flex;align-items:center;gap:14px;padding:11px 16px;${i < items.length - 1 ? 'border-bottom:1px solid #f4f4f4;' : ''}${isTop ? 'background:#fffdf5;' : ''}">
        <span style="
          width:24px;height:24px;border-radius:50%;
          background:${color};color:#fff;
          font-size:10px;font-weight:800;
          display:inline-flex;align-items:center;justify-content:center;
          flex-shrink:0;
        ">${i + 1}</span>

        <span style="flex:0 0 180px;font-weight:${isTop ? '700' : '600'};font-size:13px;color:#111;overflow:hidden;text-overflow:ellipsis;white-space:nowrap;">
          ${escapeHtml(name)}
        </span>

        <div style="flex:1;height:7px;background:#f0f0f0;border-radius:4px;overflow:hidden;">
          <div style="height:100%;width:${pct}%;background:${color};border-radius:4px;"></div>
        </div>

        <span style="flex:0 0 100px;text-align:right;font-size:12px;font-weight:700;color:#444;white-space:nowrap;">
          ${escapeHtml(count)} ${escapeHtml(unit)}
        </span>

        ${isTop ? `<span style="flex-shrink:0;padding:2px 8px;border-radius:999px;background:#FEF3C7;color:#92400E;font-size:10px;font-weight:700;">⭐ Top</span>` : '<span style="flex-shrink:0;width:52px;"></span>'}
      </div>`;
  }).join('');

  return `
    <div style="margin-bottom:28px;">
      <div style="display:flex;align-items:baseline;justify-content:space-between;margin-bottom:12px;">
        <p style="font-size:10px;text-transform:uppercase;letter-spacing:0.14em;color:#999;font-weight:700;">${escapeHtml(sectionTitle)}</p>
        <p style="font-size:11px;color:#bbb;">Consider marking top performers as Popular on your catalog</p>
      </div>
      <div style="border:1px solid #e8e8e8;border-radius:10px;overflow:hidden;">
        ${rows}
      </div>
    </div>`;
};

// ── BASE CSS ────────────────────────────────────────────────────────────────

const BASE_CSS = `
  *, *::before, *::after { box-sizing: border-box; margin: 0; padding: 0; }
  body {
    font-family: -apple-system, BlinkMacSystemFont, 'Segoe UI', Helvetica, Arial, sans-serif;
    background: #fff; color: #111; font-size: 13px; line-height: 1.55;
  }
  .page { max-width: 960px; margin: 0 auto; padding: 48px; }

  .print-bar {
    position: fixed; top: 0; left: 0; right: 0; z-index: 100;
    background: #111; padding: 0 40px;
    height: 52px; display: flex; align-items: center; justify-content: space-between;
  }
  .print-bar-left { display: flex; align-items: center; gap: 12px; }
  .print-bar-logo { color: #fff; font-size: 16px; font-weight: 800; letter-spacing: -0.3px; }
  .print-bar-sep { width: 1px; height: 18px; background: rgba(255,255,255,0.2); }
  .print-bar-title { color: rgba(255,255,255,0.6); font-size: 13px; }
  .print-btn {
    background: #fff; color: #111; border: none;
    padding: 7px 18px; border-radius: 7px;
    cursor: pointer; font-size: 13px; font-weight: 700;
  }
  .print-btn:hover { background: #f0f0f0; }

  .report-header {
    display: flex; justify-content: space-between; align-items: flex-end;
    padding-bottom: 20px; margin-bottom: 28px;
    border-bottom: 2px solid #111;
  }
  .brand { font-size: 26px; font-weight: 800; letter-spacing: -0.8px; color: #111; }
  .report-type { font-size: 13px; color: #888; margin-top: 3px; }
  .header-meta { text-align: right; font-size: 12px; color: #888; line-height: 1.8; }
  .header-meta strong { color: #333; }

  .stats { display: grid; grid-template-columns: repeat(3, 1fr); gap: 14px; margin-bottom: 28px; }
  .stat { background: #f6f6f6; border-radius: 10px; padding: 18px 20px; border: 1px solid #ebebeb; }
  .stat-label { font-size: 10px; text-transform: uppercase; letter-spacing: 0.14em; color: #999; font-weight: 700; margin-bottom: 8px; }
  .stat-value { font-size: 28px; font-weight: 800; color: #111; letter-spacing: -1px; line-height: 1; }
  .stat-sub { font-size: 11px; color: #aaa; margin-top: 4px; }

  .table-wrap { border: 1px solid #e8e8e8; border-radius: 10px; overflow: hidden; }
  table { width: 100%; border-collapse: collapse; }
  thead tr { background: #f4f4f4; }
  th {
    padding: 11px 14px; text-align: left;
    font-size: 10px; text-transform: uppercase; letter-spacing: 0.12em;
    color: #777; font-weight: 700; white-space: nowrap;
    border-bottom: 1px solid #e8e8e8;
  }
  td { padding: 12px 14px; border-bottom: 1px solid #f2f2f2; vertical-align: middle; }
  tr:last-child td { border-bottom: none; }
  tbody tr:nth-child(even) { background: #fafafa; }
  .id-cell { color: #aaa; font-size: 11px; font-variant-numeric: tabular-nums; }
  .client-name { font-weight: 600; color: #111; }
  .client-email { font-size: 11px; color: #aaa; margin-top: 1px; }
  .items-cell { font-size: 12px; color: #444; max-width: 200px; }
  .total-cell { font-weight: 700; color: #111; font-variant-numeric: tabular-nums; }

  .report-footer {
    margin-top: 28px; padding-top: 16px; border-top: 1px solid #ebebeb;
    display: flex; justify-content: space-between; align-items: center;
    font-size: 11px; color: #bbb;
  }

  @media print {
    .print-bar { display: none !important; }
    .page { padding: 32px; margin-top: 0 !important; }
    body { font-size: 12px; }
  }
`;

const createCspNonce = () => {
  const bytes = new Uint8Array(16);

  if (globalThis.crypto?.getRandomValues) {
    globalThis.crypto.getRandomValues(bytes);
  } else {
    for (let index = 0; index < bytes.length; index += 1) {
      bytes[index] = Math.floor(Math.random() * 256);
    }
  }

  return Array.from(bytes, (byte) => byte.toString(16).padStart(2, '0')).join('');
};

const shell = (title, companyName, bodyContent) => {
  const nonce = createCspNonce();

  return `<!DOCTYPE html>
<html lang="en">
<head>
  <meta charset="UTF-8" />
  <meta name="viewport" content="width=device-width, initial-scale=1.0" />
  <meta name="referrer" content="no-referrer" />
  <meta http-equiv="Content-Security-Policy" content="default-src 'none'; style-src 'unsafe-inline'; script-src 'nonce-${nonce}'; img-src data:; base-uri 'none'; form-action 'none'" />
  <title>${escapeHtml(title)} — ${escapeHtml(companyName)}</title>
  <style>${BASE_CSS}</style>
</head>
<body>
  <div class="print-bar">
    <div class="print-bar-left">
      <span class="print-bar-logo">${escapeHtml(companyName)}</span>
      <span class="print-bar-sep"></span>
      <span class="print-bar-title">${escapeHtml(title)}</span>
    </div>
    <button class="print-btn" id="print-report" type="button">⬇ Print / Save as PDF</button>
  </div>
  <div class="page" style="margin-top:68px;">
    ${bodyContent}
  </div>
  <script nonce="${nonce}">
    document.getElementById('print-report').addEventListener('click', () => window.print());
  </script>
</body>
</html>`;
};

// ── Orders report ───────────────────────────────────────────────────────────

export const generateOrdersReport = (orders = [], filters = {}, companyName = 'Claw') => {
  const safeOrders = Array.isArray(orders) ? orders : [];
  const safeFilters = filters && typeof filters === 'object' ? filters : {};
  const safeCompanyName = companyName || 'Claw';
  const orderTotal = (o) =>
    (Array.isArray(o?.items) ? o.items : [])
      .reduce((s, i) => s + toFiniteNumber(i?.unitPrice ?? i?.price) * toFiniteNumber(i?.quantity), 0);

  const activeOrders    = safeOrders.filter(o => o?.status !== 'cancelled');
  const cancelledOrders = safeOrders.filter(o => o?.status === 'cancelled');

  const activeRevenue    = activeOrders.reduce((s, o) => s + orderTotal(o), 0);
  const cancelledRevenue = cancelledOrders.reduce((s, o) => s + orderTotal(o), 0);
  const totalItems       = activeOrders.reduce((s, o) =>
    s + (Array.isArray(o?.items) ? o.items : []).reduce((sum, i) => sum + toFiniteNumber(i?.quantity), 0), 0);

  const topSection = renderTopSection(topOrderedItems(activeOrders), 'Most Ordered Items');

  const rows = safeOrders.map(o => {
    const itemsStr = (Array.isArray(o?.items) ? o.items : [])
      .map(i => `${i?.itemName ?? i?.name ?? 'Item'} ×${toFiniteNumber(i?.quantity)}`)
      .join(', ') || EMPTY_VALUE;
    const total    = orderTotal(o);
    return `<tr>
      <td class="id-cell">${escapeHtml(o?.referenceCode ?? o?.id ?? EMPTY_VALUE)}</td>
      <td>
        <div class="client-name">${escapeHtml(o?.clientName ?? EMPTY_VALUE)}</div>
        ${o?.clientEmail ? `<div class="client-email">${escapeHtml(o.clientEmail)}</div>` : ''}
      </td>
      <td>${escapeHtml(o?.clientPhone ?? EMPTY_VALUE)}</td>
      <td class="items-cell">${escapeHtml(itemsStr)}</td>
      <td class="total-cell">${fmtCurrency(total)}</td>
      <td>${escapeHtml(fmtDate(o?.date))}</td>
      <td>${badge(o?.status)}</td>
    </tr>`;
  }).join('');

  const body = `
    <div class="report-header">
      <div>
        <div class="brand">${escapeHtml(safeCompanyName)}</div>
        <div class="report-type">Orders Report</div>
      </div>
      <div class="header-meta">
        <div>Generated <strong>${escapeHtml(fmtDate(new Date().toISOString()))}</strong></div>
        <div>Filter: <strong>${escapeHtml(filterLabel(safeFilters))}</strong></div>
      </div>
    </div>

    <div class="stats">
      <div class="stat">
        <div class="stat-label">Total Orders</div>
        <div class="stat-value">${safeOrders.length}</div>
      </div>
      <div class="stat">
        <div class="stat-label">Revenue</div>
        <div class="stat-value" style="font-size:22px;">${fmtCurrency(activeRevenue)}</div>
        ${cancelledRevenue > 0 ? `
          <div class="stat-sub" style="margin-top:7px;">
            <span style="display:inline-block;padding:2px 8px;border-radius:999px;background:#FEE2E2;color:#991B1B;font-size:10px;font-weight:700;">
              − ${fmtCurrency(cancelledRevenue)} cancelled
            </span>
          </div>` : ''}
      </div>
      <div class="stat">
        <div class="stat-label">Items Sold</div>
        <div class="stat-value">${totalItems}</div>
        <div class="stat-sub">from active orders</div>
      </div>
    </div>

    ${topSection}

    <div class="table-wrap">
      <table>
        <thead>
          <tr>
            <th>Reference</th><th>Client</th><th>Phone</th><th>Items</th><th>Total</th><th>Date</th><th>Status</th>
          </tr>
        </thead>
        <tbody>
          ${rows || '<tr><td colspan="7" style="text-align:center;color:#aaa;padding:40px;">No records</td></tr>'}
        </tbody>
      </table>
    </div>

    <div class="report-footer">
      <span>${safeOrders.length} record${safeOrders.length !== 1 ? 's' : ''}</span>
      <span>Exported from Claw · ${escapeHtml(fmtDate(new Date().toISOString()))}</span>
    </div>`;

  return shell('Orders Report', safeCompanyName, body);
};

// ── Bookings report ─────────────────────────────────────────────────────────

export const generateBookingsReport = (bookings = [], filters = {}, companyName = 'Claw') => {
  const safeBookings = Array.isArray(bookings) ? bookings : [];
  const safeFilters = filters && typeof filters === 'object' ? filters : {};
  const safeCompanyName = companyName || 'Claw';
  const activeBookings    = safeBookings.filter(b => b?.status !== 'cancelled' && b?.status !== 'noshow' && b?.status !== 'no-show');
  const cancelledBookings = safeBookings.filter(b => b?.status === 'cancelled');

  const activeRevenue    = activeBookings.reduce((s, b) => s + toFiniteNumber(b?.price), 0);
  const cancelledRevenue = cancelledBookings.reduce((s, b) => s + toFiniteNumber(b?.price), 0);
  const completed        = safeBookings.filter(b => b?.status === 'completed').length;

  const topSection = renderTopSection(topBookedServices(activeBookings), 'Most Booked Services');

  const rows = safeBookings.map(b => `<tr>
    <td class="id-cell">${escapeHtml(b?.referenceCode ?? b?.id ?? EMPTY_VALUE)}</td>
    <td>
      <div class="client-name">${escapeHtml(b?.clientName ?? EMPTY_VALUE)}</div>
      ${b?.clientEmail ? `<div class="client-email">${escapeHtml(b.clientEmail)}</div>` : ''}
    </td>
    <td>${escapeHtml(b?.clientPhone ?? EMPTY_VALUE)}</td>
    <td>${escapeHtml(b?.serviceName ?? EMPTY_VALUE)}</td>
    <td>${escapeHtml(fmtDate(b?.date))}</td>
    <td>${escapeHtml(b?.time ?? EMPTY_VALUE)}</td>
    <td class="total-cell">${b?.price != null ? fmtCurrency(b.price) : EMPTY_VALUE}</td>
    <td>${badge(b?.status)}</td>
  </tr>`).join('');

  const body = `
    <div class="report-header">
      <div>
        <div class="brand">${escapeHtml(safeCompanyName)}</div>
        <div class="report-type">Bookings Report</div>
      </div>
      <div class="header-meta">
        <div>Generated <strong>${escapeHtml(fmtDate(new Date().toISOString()))}</strong></div>
        <div>Filter: <strong>${escapeHtml(filterLabel(safeFilters))}</strong></div>
      </div>
    </div>

    <div class="stats">
      <div class="stat">
        <div class="stat-label">Total Bookings</div>
        <div class="stat-value">${safeBookings.length}</div>
      </div>
      <div class="stat">
        <div class="stat-label">Revenue</div>
        <div class="stat-value" style="font-size:22px;">${fmtCurrency(activeRevenue)}</div>
        ${cancelledRevenue > 0 ? `
          <div class="stat-sub" style="margin-top:7px;">
            <span style="display:inline-block;padding:2px 8px;border-radius:999px;background:#FEE2E2;color:#991B1B;font-size:10px;font-weight:700;">
              − ${fmtCurrency(cancelledRevenue)} cancelled
            </span>
          </div>` : ''}
      </div>
      <div class="stat">
        <div class="stat-label">Completed</div>
        <div class="stat-value">${completed}</div>
        <div class="stat-sub">served</div>
      </div>
    </div>

    ${topSection}

    <div class="table-wrap">
      <table>
        <thead>
          <tr>
            <th>Reference</th><th>Client</th><th>Phone</th><th>Service</th><th>Date</th><th>Time</th><th>Total</th><th>Status</th>
          </tr>
        </thead>
        <tbody>
          ${rows || '<tr><td colspan="8" style="text-align:center;color:#aaa;padding:40px;">No records</td></tr>'}
        </tbody>
      </table>
    </div>

    <div class="report-footer">
      <span>${safeBookings.length} record${safeBookings.length !== 1 ? 's' : ''}</span>
      <span>Exported from Claw · ${escapeHtml(fmtDate(new Date().toISOString()))}</span>
    </div>`;

  return shell('Bookings Report', safeCompanyName, body);
};
